<div class="smart-grid position-relative" [class.custom-mobile]="customMobile">
  <!-- External loader (from parent component) -->
  <div class="loader" *ngIf="showLoader">
    <div class="loader-spinner">
      <div class="spinner-border" role="status">
        <span class="visually-hidden"></span>
      </div>
    </div>
  </div>

  <!-- Internal processing loader (for grouping/sorting/filtering) -->
  <div class="loader processing-loader" *ngIf="isProcessing">
    <div class="loader-spinner">
      <div class="spinner-border spinner-border-lg" role="status">
        <span class="visually-hidden"></span>
      </div>
      <div class="mt-2 text-center">
        <div class="fw-bold">Prosesserer data...</div>
        <div class="small text-muted mt-1" *ngIf="data.length > 500">{{ data.length }} rader • Dette kan ta noen sekunder</div>
      </div>
    </div>
  </div>

  <!-- Group panel (when enabled) -->
  <div *ngIf="showGroupPanel" class="group-panel">
    <div class="sg-toolbar">
      <!-- Left section: Gruppering label + chips -->
      <div class="sg-toolbar-left">
        <span class="sg-toolbar-label">Gruppering:</span>

        <ng-container *ngFor="let g of groups">
          <span class="sg-group-chip">
            {{ columnTitle(g.field) }}
            <button class="sg-chip-close" (click)="removeGroup(g.field)" title="Fjern gruppen">&times;</button>
          </span>
        </ng-container>

        <!-- Add group (compact icon select) -->
        <select class="sg-add-group-select" (change)="addGroup($any($event.target).value); $any($event.target).value = ''" title="Legg til gruppering">
          <option value="" selected>+</option>
          <option *ngFor="let c of columns" [value]="fieldKey(c.field)" [disabled]="isGroupedField(c)">
            {{ c.title }}
          </option>
        </select>
      </div>

      <!-- Center: Global search (when enabled) -->
      <div class="sg-toolbar-center" *ngIf="showGlobalSearch">
        <div class="sg-search-box">
          <i class="bi bi-search sg-search-icon"></i>
          <input type="text" class="sg-search-input" [placeholder]="globalSearchPlaceholder" [value]="globalSearchTerm" (input)="onGlobalSearch($any($event.target).value)" />
        </div>
      </div>

      <!-- Right section: Toggle, Export, Expand/Collapse, Custom content -->
      <div class="sg-toolbar-right">
        <!-- Custom toolbar content slot -->
        <ng-content select="[smart-grid-toolbar]"></ng-content>

        <!-- Aggregation filter toggle -->
        <ng-container *ngIf="aggFields.length && aggregateFilterConfig">
          <div class="sg-toolbar-toggle">
            <span class="sg-toggle-label">{{ aggregateFilterConfig.label }}</span>
            <div class="form-check form-switch mb-0">
              <input class="form-check-input" type="checkbox" id="aggFilterToggle" [checked]="aggregateFilterConfig.enabled" (change)="onAggregateFilterToggle($any($event.target).checked)" />
            </div>
          </div>
        </ng-container>

        <!-- Expand/collapse -->
        <ng-container *ngIf="groups.length">
          <div ngbDropdown class="sg-level-dropdown">
            <button class="sg-level-btn" ngbDropdownToggle title="Utvid/kollaps">
              <i class="bi bi-plus-slash-minus"></i>
            </button>
            <div ngbDropdownMenu>
              <button ngbDropdownItem (click)="expandAll()">Utvid alle</button>
              <button ngbDropdownItem (click)="collapseAll()">Kollaps alle</button>
              <div class="dropdown-divider" *ngIf="groups.length > 1"></div>
              <ng-container *ngFor="let g of groups; index as i">
                <button ngbDropdownItem (click)="expandAll(i)">Utvid {{ columnTitle(g.field) }}</button>
                <button ngbDropdownItem (click)="collapseAll(i)">Kollaps {{ columnTitle(g.field) }}</button>
              </ng-container>
            </div>
          </div>
        </ng-container>

        <!-- Export dropdown -->
        <ng-container *ngIf="enableExport">
          <div ngbDropdown class="sg-export-dropdown">
            <button class="sg-export-btn" ngbDropdownToggle [disabled]="!data || data.length === 0">
              <i class="bi bi-file-earmark-excel sg-excel-icon"></i> {{ exportButtonLabel }} <i class="bi bi-chevron-down ms-1" style="font-size:0.65rem"></i>
            </button>
            <div ngbDropdownMenu>
              <button ngbDropdownItem (click)="exportToExcelRaw()">
                <i class="bi bi-table me-2"></i> Uten formattering
              </button>
              <button ngbDropdownItem (click)="exportToExcelFormatted()">
                <i class="bi bi-file-earmark-richtext me-2"></i> Med formattering
              </button>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  </div>

  <!-- Standalone toolbar (when groupPanel is disabled but export is enabled) -->
  <div *ngIf="!showGroupPanel && enableExport" class="mb-2 d-flex justify-content-end">
    <div ngbDropdown class="sg-export-dropdown">
      <button class="sg-export-btn" ngbDropdownToggle [disabled]="!data || data.length === 0">
        <i class="bi bi-file-earmark-excel sg-excel-icon"></i> {{ exportButtonLabel }} <i class="bi bi-chevron-down ms-1" style="font-size:0.65rem"></i>
      </button>
      <div ngbDropdownMenu>
        <button ngbDropdownItem (click)="exportToExcelRaw()">
          <i class="bi bi-table me-2"></i> Uten formattering
        </button>
        <button ngbDropdownItem (click)="exportToExcelFormatted()">
          <i class="bi bi-file-earmark-richtext me-2"></i> Med formattering
        </button>
      </div>
    </div>
  </div>

  <!-- Active filter chips -->
  <div class="sg-filter-chips" *ngIf="filters.length > 0">
    <span class="sg-filter-chips-label">Filtre:</span>
    <ng-container *ngFor="let chip of getActiveFiltersForChips()">
      <span class="sg-filter-chip">
        <span class="sg-filter-chip-text">{{ chip.label }}</span>
        <button class="sg-filter-chip-remove" (click)="removeFilterByField(chip.field)">&times;</button>
      </span>
    </ng-container>
    <button class="sg-filter-chips-clear" (click)="clearFilters()">Fjern alle</button>
  </div>

  <!-- Grid -->
  <div class="table-responsive">
    <!-- ===== DESKTOP TABLE (hidden on mobile for non-custom-mobile grids) ===== -->
    <table class="table table-striped2 table-sm align-middle table-fixed" [class.sg-desktop-table]="!customMobile">
      <thead>
        <tr>
          <th *ngFor="let col of visibleColumns()" [style.width]="colWidth(col)" [style.text-align]="col.align || 'left'"
              class="sg-th" [class.sg-th-active]="hasActiveFilter(col) || isSortedAsc(col.field) || isSortedDesc(col.field)"
              [class.sg-th-resizable]="isColumnResizable(col)">
            <div class="sg-header-content">
              <span class="sg-header-title" [class.cursor-pointer]="col.sortable"
                    (click)="onHeaderClick($event, col.field, col.sortable)">{{ col.title }}</span>
              <span class="sg-header-icons">
                <i *ngIf="col.sortable" class="bi sg-sort-icon" [ngClass]="chevronClass(col)"
                   (click)="onHeaderClick($event, col.field, col.sortable)"></i>
                <button *ngIf="col.filterable && showFilterPanel" class="sg-filter-trigger"
                        [class.active]="hasActiveFilter(col) || filterRowVisible"
                        (click)="$event.stopPropagation(); toggleFilterRow()">
                  <i class="bi" [ngClass]="hasActiveFilter(col) ? 'bi-funnel-fill' : 'bi-funnel'"></i>
                </button>
                <input *ngIf="!col.filterable && checkedColumn === fieldKey(col.field)" type="checkbox"
                       class="form-check-input cursor-pointer sg-select-all-cb" (change)="changeData($event)"
                       [(ngModel)]="checkedAllCheckBox" title="Velg alle synlige rader">
              </span>
            </div>
            <!-- Drag-handle for column resize. mousedown.stop hindrer at klikket
                 trigger sort-handler på header-tittelen. -->
            <span *ngIf="isColumnResizable(col)"
                  class="sg-resize-handle"
                  (mousedown)="onResizeStart($event, col)"
                  (click)="$event.stopPropagation()"
                  aria-hidden="true"></span>
          </th>
        </tr>
        <!-- Inline filter row -->
        <tr *ngIf="filterRowVisible && showFilterPanel" class="sg-filter-row">
          <td *ngFor="let col of visibleColumns()" class="sg-filter-cell" [style.text-align]="col.align || 'left'">
            <ng-container *ngIf="col.filterable">
              <!-- Operator dropdown (text/number/date) -->
              <select *ngIf="col.filterType !== 'select'" class="form-select form-select-sm sg-filter-op-select"
                      [(ngModel)]="filterType[col.field.toString() + col.filterType.toString()]"
                      (ngModelChange)="onFilterOperatorChange(col.field, col.filterType, filterType[col.field.toString() + col.filterType.toString()])">
                <option *ngFor="let o of (col.filterType === 'text' ? textFilter : col.filterType === 'number' ? numberFilter : dateFilter)" [value]="o.value">{{ o.label }}</option>
              </select>
              <!-- Text -->
              <input *ngIf="col.filterType === 'text'" type="text" class="form-control form-control-sm sg-filter-input"
                     [(ngModel)]="filterValue[col.field.toString() + col.filterType.toString()]"
                     (input)="setFilterDebounced(col.field, 'text', $any($event.target).value, filterType[col.field.toString() + col.filterType.toString()])"
                     placeholder="Søk..." />
              <!-- Number -->
              <input *ngIf="col.filterType === 'number'" type="number" class="form-control form-control-sm sg-filter-input"
                     [(ngModel)]="filterValue[col.field.toString() + col.filterType.toString()]"
                     (input)="setFilterDebounced(col.field, 'number', $any($event.target).value, filterType[col.field.toString() + col.filterType.toString()])"
                     placeholder="Verdi..." />
              <!-- Date -->
              <ng-container *ngIf="col.filterType === 'date'">
                <input type="date" *ngIf="bsConfig == null" class="form-control form-control-sm sg-filter-input"
                       [(ngModel)]="filterValue[col.field.toString() + col.filterType.toString()]"
                       (change)="setFilter(col.field, 'date', $any($event.target).value, filterType[col.field.toString() + col.filterType.toString()])" />
                <div class="input-group input-group-sm" *ngIf="bsConfig">
                  <input type="text" class="form-control form-control-sm month-picker-height"
                         [bsConfig]="bsConfig" #dpFromDate="bsDatepicker" bsDatepicker
                         [(ngModel)]="filterValue[col.field.toString() + col.filterType.toString()]"
                         (keydown)="$event.preventDefault()"
                         (ngModelChange)="setFilter(col.field, 'date', $event, filterType[col.field.toString() + col.filterType.toString()])">
                  <button class="btn btn-outline-secondary btn-sm bi bi-calendar3" (click)="dpFromDate.toggle()" type="button"></button>
                </div>
              </ng-container>
              <!-- Select -->
              <select *ngIf="col.filterType === 'select'" class="form-select form-select-sm sg-filter-input"
                      [(ngModel)]="filterValue[col.field.toString() + col.filterType.toString()]"
                      (change)="setFilter(col.field, 'select', $any($event.target).value, 'eq')">
                <option value="" *ngIf="col.removeDefaultOption == false || col.removeDefaultOption == undefined">Alle</option>
                <option *ngFor="let o of col.selectOptions" [value]="o.value">{{ o.label }}</option>
              </select>
              <!-- Clear single column filter -->
              <button *ngIf="hasActiveFilter(col)" class="btn btn-sm sg-filter-clear-btn" (click)="removeFilterByField(fieldKey(col.field))"
                      title="Fjern filter">
                <i class="bi bi-x-circle"></i>
              </button>
            </ng-container>
          </td>
        </tr>
      </thead>

      <!-- Flat mode -->
      <tbody *ngIf="!isGrouped()">
        <tr *ngFor="let row of rows; trackBy: trackByRow" (click)="rowClick.emit(row)" class="grid-row" [ngClass]="rowClass ? rowClass(row) : null">
          <td *ngFor="let col of visibleColumns()" [ngClass]="col.className" [style.text-align]="col.align || 'left'" [attr.data-label]="customMobile ? null : col.title" [attr.title]="cellTooltip(row, col)" (dblclick)="col.editable ? startEdit(row, col) : null" [class.editable-cell]="col.editable" [class.editing]="isCellEditing(row, col)">

            <!-- Editing mode -->
            <ng-container *ngIf="isCellEditing(row, col)">
              <!-- Custom edit template -->
              <ng-container *ngIf="col.editType === 'custom' && editTemplateMap.has(fieldKey(col.field)); else standardEdit">
                <ng-container [ngTemplateOutlet]="editTemplateMap.get(fieldKey(col.field))?.template"
                  [ngTemplateOutletContext]="{ $implicit: row, value: editingValue, save: saveEditFn(row, col), cancel: cancelEditFn() }">
                </ng-container>
              </ng-container>

              <ng-template #standardEdit>
              <div class="d-flex align-items-center gap-1">
                <!-- Text input -->
                <input *ngIf="!col.editType || col.editType === 'text'" type="text" class="form-control form-control-sm" [(ngModel)]="editingValue" (keydown.enter)="saveEdit(row, col)" (keydown.escape)="cancelEdit()" [class.is-invalid]="editError" autofocus />

                <!-- Number input -->
                <input *ngIf="col.editType === 'number'" type="number" class="form-control form-control-sm" [(ngModel)]="editingValue" [min]="col.editMin" [max]="col.editMax" step="0.01" (keydown.enter)="saveEdit(row, col)" (keydown.escape)="cancelEdit()" [class.is-invalid]="editError" autofocus />

                <!-- Date input -->
                <input *ngIf="col.editType === 'date'" type="date" class="form-control form-control-sm" [(ngModel)]="editingValue" (keydown.enter)="saveEdit(row, col)" (keydown.escape)="cancelEdit()" [class.is-invalid]="editError" autofocus />

                <!-- Select input -->
                <select *ngIf="col.editType === 'select'" class="form-select form-select-sm" [(ngModel)]="editingValue" (keydown.enter)="saveEdit(row, col)" (keydown.escape)="cancelEdit()" [class.is-invalid]="editError" autofocus>
                  <option *ngFor="let opt of col.editSelectOptions" [value]="opt.value">
                    {{ opt.label }}
                  </option>
                </select>

                <!-- Save/Cancel buttons -->
                <button class="btn btn-sm btn-success px-2 py-0" (click)="saveEdit(row, col)" title="Lagre">
                  <i class="bi bi-check-lg"></i>
                </button>
                <button class="btn btn-sm btn-secondary px-2 py-0" (click)="cancelEdit()" title="Avbryt">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>

              <!-- Validation error message -->
              <div *ngIf="editError" class="invalid-feedback d-block">
                {{ editError }}
              </div>
              </ng-template>
            </ng-container>

            <!-- Display mode (not editing) -->
            <ng-container *ngIf="!isCellEditing(row, col)">
              <div class="cell-content">
                <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else customFlat">
                  {{ formatValue(row, col) }}
                </ng-container>
                <ng-template #customFlat>
                  <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: row }">
                  </ng-container>
                </ng-template>

                <!-- Edit indicator icon for editable cells -->
                <i *ngIf="col.editable" class="bi bi-pencil-square ms-1 text-muted edit-icon"></i>
              </div>
            </ng-container>
          </td>
        </tr>
        <tr *ngIf="rows.length == 0 && noRecordsFoundPanel">
          <td [attr.colspan]="visibleColumns().length">{{noRecordsFoundText}}</td>
        </tr>
      </tbody>

      <!-- Grouped mode -->
      <tbody *ngIf="isGrouped()">
        <ng-container *ngFor="let node of showPagination ? paginatedTree : processedTree">
          <ng-template [ngTemplateOutlet]="groupOrRow" [ngTemplateOutletContext]="{ $implicit: node, level: 0 }">
          </ng-template>
        </ng-container>
      </tbody>

      <!-- Total row (when enabled) -->
      <tfoot *ngIf="showTotalRow && aggFields.length && !showFieldSum" class="sg-total-footer">
        <tr>
          <td [attr.colspan]="visibleColumns().length">
            <div class="sg-total-bar">
              <span class="sg-total-label">Totalsum</span>
              <div class="sg-total-values">
                <ng-container *ngFor="let f of aggFields">
                  <ng-container *ngFor="let kind of groupAggregates[f]">
                    <span class="sg-total-pill" *ngIf="totalAggVal(f, kind) !== undefined">
                      {{ totalAggVal(f, kind) | number:'0.2-2' }}
                    </span>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>

      <tfoot *ngIf="showTotalRow && aggFields.length && showFieldSum" class="sg-total-footer">
        <tr class="total-row">
          <td *ngFor="let col of visibleColumns();let i = index" class="fw-bold" [ngClass]="col.className" [style.text-align]="col.align || 'left'" [attr.data-label]="customMobile ? null : col.title">
            <ng-container *ngIf="i==0">
              <span class="sg-total-label-inline">Totalt</span>
            </ng-container>
            <ng-container *ngFor="let f of aggFields">
              <ng-container *ngIf="f==col.field">
                <ng-container *ngFor="let kind of groupAggregates[f]">
                  <span class="sg-total-pill" *ngIf="totalAggVal(f, kind) !== undefined">
                    {{ totalAggVal(f, kind) | number:'0.2-2' }}
                  </span>
                </ng-container>
              </ng-container>
            </ng-container>
          </td>
        </tr>
      </tfoot>
    </table>

    <!-- ===== MOBILE CARD LAYOUT (shown on mobile for non-custom-mobile grids) ===== -->
    <div class="sg-auto-mobile-cards" *ngIf="!customMobile">
      <!-- Flat mode -->
      <ng-container *ngIf="!isGrouped()">
        <div class="sg-mobile-card" *ngFor="let row of rows; trackBy: trackByRow" (click)="rowClick.emit(row)" [ngClass]="rowClass ? rowClass(row) : null">
          <div class="sg-mobile-card-body">
            <div class="sg-mobile-card-content">
              <!-- Badges row -->
              <div class="sg-mobile-card-badges" *ngIf="getMobileBadgeCols().length">
                <ng-container *ngFor="let col of getMobileBadgeCols()">
                  <span class="sg-mobile-badge">
                    <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else mobileBadgeTpl">
                      {{ formatValue(row, col) }}
                    </ng-container>
                    <ng-template #mobileBadgeTpl>
                      <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
                    </ng-template>
                  </span>
                </ng-container>
              </div>

              <!-- Title row with expand chevron -->
              <div class="sg-mobile-card-title-row">
                <ng-container *ngIf="getMobileTitleCol() as titleCol">
                  <div class="sg-mobile-card-title">
                    <ng-container *ngIf="!templateMap.has(fieldKey(titleCol.field)); else mobileTitleTpl">
                      {{ formatValue(row, titleCol) }}
                    </ng-container>
                    <ng-template #mobileTitleTpl>
                      <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(titleCol.field))?.template" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
                    </ng-template>
                  </div>
                </ng-container>
                <div class="sg-mobile-card-chevron" *ngIf="hasMobileDetails()" (click)="toggleMobileExpand(row); $event.stopPropagation()">
                  <i class="bi" [ngClass]="isMobileExpanded(row) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </div>
              </div>

              <!-- Meta info (subtitle, dates, counts) -->
              <div class="sg-mobile-card-meta">
                <ng-container *ngFor="let col of getMobileHeaderCols()">
                  <ng-container *ngIf="getMobileRole(col) === 'meta' || getMobileRole(col) === 'subtitle'">
                    <span class="sg-mobile-meta-item">
                      <span class="sg-mobile-meta-label">{{ col.title }}</span>
                      <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else mobileMetaTpl">
                        <span class="sg-mobile-meta-value">{{ formatValue(row, col) }}</span>
                      </ng-container>
                      <ng-template #mobileMetaTpl>
                        <span class="sg-mobile-meta-value">
                          <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
                        </span>
                      </ng-template>
                    </span>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </div>

          <!-- Expandable details -->
          <div class="sg-mobile-card-details" *ngIf="isMobileExpanded(row)">
            <ng-container *ngFor="let col of getMobileDetailCols()">
              <div class="sg-mobile-detail-row">
                <span class="sg-mobile-detail-label">{{ col.title }}</span>
                <span class="sg-mobile-detail-value">
                  <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else mobileDetailTpl">
                    {{ formatValue(row, col) }}
                  </ng-container>
                  <ng-template #mobileDetailTpl>
                    <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
                  </ng-template>
                </span>
              </div>
            </ng-container>
          </div>

          <!-- Footer: actions -->
          <ng-container *ngIf="getMobileActionsCol() as actionsCol">
            <div class="sg-mobile-card-footer" (click)="$event.stopPropagation()">
              <ng-container *ngIf="templateMap.has(fieldKey(actionsCol.field))">
                <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(actionsCol.field))?.template" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
              </ng-container>
            </div>
          </ng-container>
        </div>

        <div class="sg-mobile-no-records" *ngIf="rows.length === 0 && noRecordsFoundPanel">
          {{ noRecordsFoundText }}
        </div>
      </ng-container>

      <!-- Grouped mode -->
      <ng-container *ngIf="isGrouped()">
        <ng-container *ngFor="let node of showPagination ? paginatedTree : processedTree">
          <ng-template [ngTemplateOutlet]="mobileGroupOrRow" [ngTemplateOutletContext]="{ $implicit: node, level: 0 }"></ng-template>
        </ng-container>
      </ng-container>
    </div>
  </div>

  <!-- Mobile group/row recursive template -->
  <ng-template #mobileGroupOrRow let-node let-level="level">
    <!-- Group header -->
    <div *ngIf="node.kind==='group'" class="sg-mobile-group-header" [style.marginLeft.px]="level * 12" (click)="toggleGroupNode(node)">
      <i class="bi" [ngClass]="node.collapsed ? 'bi-caret-right-fill' : 'bi-caret-down-fill'"></i>
      <span class="sg-mobile-group-field">{{ columnTitle(node.field) }}:</span>
      <span class="sg-mobile-group-value">
        <ng-container *ngIf="node.field==='date' && asDate(node.value); else mobileGroupPlain">
          {{ asDate(node.value) | date:'dd.MM.yyyy' }}
        </ng-container>
        <ng-template #mobileGroupPlain>{{ node.value }}</ng-template>
      </span>
      <span class="sg-mobile-group-count">({{ node.count }})</span>
      <div class="sg-mobile-group-aggs" *ngIf="aggFields.length">
        <ng-container *ngFor="let f of aggFields">
          <ng-container *ngFor="let kind of groupAggregates[f]">
            <span class="sg-mobile-agg" *ngIf="aggVal(node, f, kind) !== undefined">
              {{ aggVal(node, f, kind) | number:'0.2-2' }}
            </span>
          </ng-container>
        </ng-container>
      </div>
    </div>

    <!-- Group children -->
    <ng-container *ngIf="node.kind==='group' && !node.collapsed">
      <ng-container *ngFor="let child of node.items">
        <ng-template [ngTemplateOutlet]="mobileGroupOrRow" [ngTemplateOutletContext]="{ $implicit: child, level: level + 1 }"></ng-template>
      </ng-container>
    </ng-container>

    <!-- Leaf row (mobile card) -->
    <ng-container *ngIf="node.kind==='row'">
      <div class="sg-mobile-card" [style.marginLeft.px]="level * 12" (click)="rowClick.emit(node.row)" [ngClass]="rowClass ? rowClass(node.row) : null">
        <div class="sg-mobile-card-body">
          <div class="sg-mobile-card-content">
            <div class="sg-mobile-card-badges" *ngIf="getMobileBadgeCols().length">
              <ng-container *ngFor="let col of getMobileBadgeCols()">
                <span class="sg-mobile-badge">
                  <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else mobileGrpBadgeTpl">
                    {{ formatValue(node.row, col) }}
                  </ng-container>
                  <ng-template #mobileGrpBadgeTpl>
                    <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: node.row }"></ng-container>
                  </ng-template>
                </span>
              </ng-container>
            </div>

            <!-- Title row with expand chevron -->
            <div class="sg-mobile-card-title-row">
              <ng-container *ngIf="getMobileTitleCol() as titleCol">
                <div class="sg-mobile-card-title">
                  <ng-container *ngIf="!templateMap.has(fieldKey(titleCol.field)); else mobileGrpTitleTpl">
                    {{ formatValue(node.row, titleCol) }}
                  </ng-container>
                  <ng-template #mobileGrpTitleTpl>
                    <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(titleCol.field))?.template" [ngTemplateOutletContext]="{ $implicit: node.row }"></ng-container>
                  </ng-template>
                </div>
              </ng-container>
              <div class="sg-mobile-card-chevron" *ngIf="hasMobileDetails()" (click)="toggleMobileExpand(node.row); $event.stopPropagation()">
                <i class="bi" [ngClass]="isMobileExpanded(node.row) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </div>
            </div>

            <div class="sg-mobile-card-meta">
              <ng-container *ngFor="let col of getMobileHeaderCols()">
                <ng-container *ngIf="getMobileRole(col) === 'meta' || getMobileRole(col) === 'subtitle'">
                  <span class="sg-mobile-meta-item">
                    <span class="sg-mobile-meta-label">{{ col.title }}</span>
                    <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else mobileGrpMetaTpl">
                      <span class="sg-mobile-meta-value">{{ formatValue(node.row, col) }}</span>
                    </ng-container>
                    <ng-template #mobileGrpMetaTpl>
                      <span class="sg-mobile-meta-value">
                        <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: node.row }"></ng-container>
                      </span>
                    </ng-template>
                  </span>
                </ng-container>
              </ng-container>
            </div>
          </div>
        </div>

        <div class="sg-mobile-card-details" *ngIf="isMobileExpanded(node.row)">
          <ng-container *ngFor="let col of getMobileDetailCols()">
            <div class="sg-mobile-detail-row">
              <span class="sg-mobile-detail-label">{{ col.title }}</span>
              <span class="sg-mobile-detail-value">
                <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else mobileGrpDetailTpl">
                  {{ formatValue(node.row, col) }}
                </ng-container>
                <ng-template #mobileGrpDetailTpl>
                  <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: node.row }"></ng-container>
                </ng-template>
              </span>
            </div>
          </ng-container>
        </div>

        <!-- Footer: actions -->
        <ng-container *ngIf="getMobileActionsCol() as actionsCol">
          <div class="sg-mobile-card-footer" (click)="$event.stopPropagation()">
            <ng-container *ngIf="templateMap.has(fieldKey(actionsCol.field))">
              <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(actionsCol.field))?.template" [ngTemplateOutletContext]="{ $implicit: node.row }"></ng-container>
            </ng-container>
          </div>
        </ng-container>
      </div>
    </ng-container>
  </ng-template>
  <div class="d-flex justify-content-end gap-2 mt-2" *ngIf="showResetButton">
    <button class="btn btn-sm btn-action-outline" (click)="clearFilters()" [disabled]="!filters.length" title="Nullstill filtre">
      <i class="bi bi-funnel"></i><i class="bi bi-x-lg ms-1"></i>
    </button>
    <button class="btn btn-sm btn-action-outline" (click)="clearSorts()" [disabled]="!sorts.length" title="Nullstill sortering">
      <i class="bi bi-sort-down"></i><i class="bi bi-x-lg ms-1"></i>
    </button>
  </div>

  <!-- recursive -->
  <ng-template #groupOrRow let-node let-level="level">
    <!-- group header -->
    <tr *ngIf="node.kind==='group'" class="sg-group-row" [ngClass]="{
      'sg-group-level-0': level===0,
      'sg-group-level-1': level===1,
      'sg-group-level-2': level>=2,
      'week-group': node.field==='week',
      'date-group': node.field==='date'
    }">
      <td [attr.colspan]="visibleColumns().length" (click)="toggleGroupNode(node)" class="user-select-none">
        <div class="sg-group-bar" [style.paddingLeft.px]="level * 20">
          <span class="sg-group-label">
            <i class="bi sg-group-caret" [ngClass]="node.collapsed ? 'bi-caret-right-fill' : 'bi-caret-down-fill'"></i>
            <span class="sg-group-field">{{ columnTitle(node.field) }}:</span>
            <span class="sg-group-value">
              <ng-container *ngIf="node.field==='date' && asDate(node.value); else plainVal">
                {{ asDate(node.value) | date:'dd.MM.yyyy' }}
              </ng-container>
              <ng-template #plainVal>{{ node.value }}</ng-template>
            </span>
            <span class="sg-group-count">({{ node.count }})</span>
          </span>
          <div class="sg-group-aggs" *ngIf="aggFields.length">
            <ng-container *ngFor="let f of aggFields">
              <ng-container *ngFor="let kind of groupAggregates[f]">
                <span class="sg-agg-badge" [ngClass]="{ 'sg-agg-badge-top': level===0 }" *ngIf="aggVal(node, f, kind) !== undefined">
                  <ng-container *ngIf="level===0">Sum {{ columnTitle(f) }} </ng-container>{{ aggVal(node, f, kind) | number:'0.2-2' }}
                </span>
              </ng-container>
            </ng-container>
          </div>
        </div>
      </td>
    </tr>

    <!-- group children -->
    <ng-container *ngIf="node.kind==='group' && !node.collapsed">
      <ng-container *ngFor="let child of node.items">
        <ng-template [ngTemplateOutlet]="groupOrRow" [ngTemplateOutletContext]="{ $implicit: child, level: level + 1 }">
        </ng-template>
      </ng-container>
    </ng-container>

    <!-- leaf row -->
    <tr *ngIf="node.kind==='row'" (click)="rowClick.emit(node.row)" class="grid-row" [ngClass]="rowClass ? rowClass(node.row) : null">
      <td *ngFor="let col of visibleColumns()" [attr.data-label]="customMobile ? null : col.title" [style.text-align]="col.align || 'left'" [attr.title]="cellTooltip(node.row, col)">
        <ng-container *ngIf="!templateMap.has(fieldKey(col.field)); else customGrouped">
          {{ formatValue(node.row, col) }}
        </ng-container>
        <ng-template #customGrouped>
          <ng-container [ngTemplateOutlet]="templateMap.get(fieldKey(col.field))?.template" [ngTemplateOutletContext]="{ $implicit: node.row }">
          </ng-container>
        </ng-template>
      </td>
    </tr>
  </ng-template>

  <div class="sg-pagination-bar" *ngIf="totalRows > 0 && showPagination">
    <div class="sg-pagination-left">
      <ngb-pagination size="sm" [collectionSize]="totalRows" [maxSize]="5" [(page)]="currentPage" [pageSize]="pageSize" [boundaryLinks]="true" (pageChange)="loadData()">
      </ngb-pagination>
    </div>
    <div class="sg-pagination-right">
      <label class="sg-page-size-label">Rader:</label>
      <select class="sg-page-size-select" [(ngModel)]="pageSize" (ngModelChange)="changePageSize()">
        <option *ngFor="let page of pageSizes" [value]="page">{{page}}</option>
      </select>
    </div>
  </div>
</div>